<script setup>
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import OrderType from '@/components/cart/orderType/OrderType.vue'
import MobileOrderType from '@/components/cart/orderType/MobileOrderType.vue'
import PaymentType from '@/components/cart/paymentType/PaymentType.vue'
import MobilePaymentType from '@/components/cart/paymentType/MobilePaymentType.vue'
import CartItems from '@/components/cart/cartItems/CartItems.vue'
import CartTotal from '@/components/cart/cartTotal/CartTotal.vue'
import { removeAllItemFn } from '@/services/api/restApiCalls'
import { useRouter } from 'vue-router'
import { ShoppingCartIcon } from '@heroicons/vue/20/solid'
import { inject } from 'vue'
const router = useRouter()
const restoStore = useRestoGLobalStore()
const cartStore = useCartGLobalStore()
const authStore = useAuthGlobalStore()
const IS_MOBILE_APP = inject('IS_MOBILE_APP')

const removeAllItems = async () => {
  const removeAllObj = {
    action: 'clear',
    resto_id: restoStore?.RESTO_ID
  }
  let removeAllFormData = new FormData()
  removeAllFormData.append('cart_data', JSON.stringify(removeAllObj))
  const cart_info = await removeAllItemFn(removeAllFormData)
  cartStore.CART_DATA = cart_info[restoStore?.RESTO_ID]
  cartStore.CAN_DO_CHECKOUT = cart_info?.can_do_checkout
}
const handleOrderCheckOut = () => {
  if (cartStore.CART_DATA?.order_type == 'delivery' && !cartStore?.CART_DATA?.pc_data?.is_valid) {
    cartStore.SHOW_POSTCODE_MODAL = true
    return
  }
  if (authStore.isLoggedIn) {
    router.push({ name: 'order_options' })
  } else {
    router.push({ name: 'login', query: { redirect: '/resto/order_options' } })
  }
}
</script>
<template>
  <div
    class="md:sticky md:top-20 md:rounded-lg border border-gray-300 dark:border-gray-700 shadow-md bg-white dark:bg-surface-dark z-30 md:z-auto hidden md:block transition-all duration-300 hover:shadow-lg"
  >
    <div class="relative cart-container px-3 lg:px-4 pt-5 pb-3">
      <div class="flex items-baseline w-full justify-between">
        <h2
          class="pb-3 md:text-2xl sm:text-xl text-lg font-bold text-text-light dark:text-text-dark flex items-center"
        >
          <ShoppingCartIcon class="h-6 w-6 mr-2" />
          Your Cart
        </h2>
        <button
          v-if="cartStore?.CART_DATA?.items?.length > 0"
          type="button"
          class="text-sm text-red-600 dark:text-red-400 font-medium py-1 hover:underline transition-all duration-200"
          @click="removeAllItems"
        >
          Clear all
        </button>
      </div>
      <!-- minimum order on delivery/collection -->
      <div
        v-if="cartStore?.CART_DATA?.promo_min_order_text"
        class="bg-gradient-to-r from-red-500 to-red-600 dark:from-indigo-700 dark:to-indigo-800 text-white rounded-md text-sm font-medium px-3 py-2 whitespace-nowrap mb-2 transition-colors duration-200 shadow-sm"
      >
        {{ cartStore?.CART_DATA?.promo_min_order_text }}
      </div>
      <div class="md:block hidden">
        <!-- Order type -->
        <OrderType></OrderType>
        <!-- Payment type -->
        <PaymentType></PaymentType>
      </div>
      <!-- Cart items -->
      <hr class="w-full mt-3 mb-2 dark:border-gray-700" />
      <div class="cart-items">
        <CartItems></CartItems>
      </div>
      <!-- Cart total -->
      <hr class="w-full my-2 dark:border-gray-700" />
      <CartTotal></CartTotal>
    </div>
    <div
      class="md:sticky bottom-0 w-full z-10 mx-auto bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-200"
    >
      <button
        type="submit"
        :disabled="cartStore?.CAN_DO_CHECKOUT == 0"
        class="w-full flex justify-between rounded-md border border-transparent bg-gradient-to-r from-violet-600 to-violet-700 dark:from-violet-700 dark:to-violet-800 py-3 px-3 lg:px-5 text-sm lg:text-base font-medium text-white shadow-sm hover:from-violet-700 hover:to-violet-800 dark:hover:from-violet-600 dark:hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-400 dark:disabled:text-gray-500 transition-all duration-300 transform hover:scale-[1.01]"
        @click="handleOrderCheckOut()"
      >
        <span class="flex items-center"
          ><ShoppingCartIcon class="sm:h-5 h-4 sm:w-5 w-4 mr-2" aria-hidden="true" />{{
            cartStore?.CART_DATA?.order_total_item_quantity || 0
          }}</span
        >
        <span><span class="hidden lg:inline">Go To </span>Checkout</span>
        <span>£{{ cartStore?.CART_DATA?.order_total || 0 }}</span>
      </button>
    </div>
  </div>
  <div class="md:hidden block">
    <div
      class="z-10 left-0 right-0 mx-auto border-t dark:border-gray-700 sm:px-5 px-4 md:py-3 py-2 bg-white dark:bg-surface-dark fixed transition-all duration-300 shadow-lg"
      :class="[IS_MOBILE_APP ? 'bottom-5 rounded-lg mx-2' : 'bottom-0']"
    >
      <div class="w-full mb-2">
        <MobileOrderType></MobileOrderType>
        <MobilePaymentType></MobilePaymentType>
      </div>
      <div class="flex w-full">
        <button
          type="submit"
          class="w-full flex justify-between rounded-md border border-transparent bg-gradient-to-r from-violet-600 to-violet-700 dark:from-violet-700 dark:to-violet-800 py-2.5 px-5 text-base font-medium text-white shadow-sm hover:from-violet-700 hover:to-violet-800 dark:hover:from-violet-600 dark:hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-400 dark:disabled:text-gray-500 transition-all duration-300"
          @click="
            $router.push({
              name: 'view_mobile_cart'
            })
          "
        >
          <span class="flex items-center"
            ><ShoppingCartIcon
              class="sm:h-5 h-4 sm:w-5 w-4 mr-2 animate-pulse"
              aria-hidden="true"
            />{{ cartStore?.CART_DATA?.order_total_item_quantity || 0 }}</span
          >
          <span>View basket</span>
          <span>£{{ cartStore?.CART_DATA?.order_total || 0 }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped>
@media only screen and (max-width: 768px) {
  .show-cart-mobile {
    position: absolute;
    left: 0;
    right: 0;
    display: block;
    height: calc(100vh - 130px);
    top: -10px;
    width: 100%;
    overflow: auto;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
}
.cart-container {
  height: calc(100vh - 190px);
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(107, 70, 193, 0.5) transparent;
}
.cart-container::-webkit-scrollbar {
  width: 6px;
}
.cart-container::-webkit-scrollbar-track {
  background: transparent;
}
.cart-container::-webkit-scrollbar-thumb {
  background-color: rgba(107, 70, 193, 0.5);
  border-radius: 20px;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
